import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useCallback, useState } from 'react';
import AuthService from '../http/AuthService';

interface AuthContextValue {
    error: string | null;
    isLoading: boolean;
    user: any | null;
    role: string;
    isAuth: boolean;
    check: () => Promise<void>;
    logIn: (email: string, password: string) => Promise<void>;
    logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
}

export function AuthProvider({ children }: PropsWithChildren) {
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [role, setRole] = useState<string>('');
    const [isAuth, setIsAuth] = useState(false);
    const [isLoading, setIsLoading] = useState(!!localStorage.getItem('token'));

    // Метод проверки авторизации
    const check = useCallback(async () => {
        if (!localStorage.getItem('token')) {
            setIsLoading(false);
            setIsAuth(false);
            return;
        }
        setIsLoading(true);
        try {
            // Если токен протух, Interceptor (Api.ts) сходит за новым
            // и вернет успешный ответ сюда.
            const { data } = await AuthService.check();

            if (data && data.user) {
                setUser(data.user);
                setRole(data.user.role);
                setIsAuth(true);
            }
        } catch (err: any) {
            // Сюда попадем только если Refresh Token в куках тоже протух
            setIsAuth(false);
            setUser(null);
            setRole('');
            localStorage.removeItem('token');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logIn = useCallback(async (email: string, password: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { data } = await AuthService.Login({ email, password });
            localStorage.setItem('token', data.accessToken);
            setUser(data.user);
            setRole(data.user.role);
            setIsAuth(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка входа');
            setIsAuth(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logOut = useCallback(async () => {
        try {
            await AuthService.Logout();
        } finally {
            localStorage.removeItem('token');
            setIsAuth(false);
            setUser(null);
            setRole('');
            window.location.href = '/';
        }
    }, []);

    useEffect(() => {
        // Следим за изменениями в других вкладках
        const handleStorage = async (e: StorageEvent) => {
            if (e.key === 'token') {
                if (!e.newValue) {
                    window.location.reload();
                    return;
                }
                await check();
            }
        };

        window.addEventListener('storage', handleStorage);

        if (!isAuth && !error && localStorage.getItem('token')) {
           void check();
        } else if (!localStorage.getItem('token')) {
            setIsLoading(false); // Останавливаем бесконечный спиннер, если токена нет
        }

        return () => window.removeEventListener('storage', handleStorage);
    }, [check, isAuth, error]);

    const value = useMemo(() => ({
        error, user, role, isAuth, isLoading, check, logIn, logOut
    }), [error, user, role, isAuth, isLoading, check, logIn, logOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
